import { Box, Button, HStack, IconButton, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text, VStack } from "@chakra-ui/react";
import { Close } from "@mui/icons-material";
import React from "react";
import apiClient from "../../clients/apiClient.js";

const CreateGroupModal = ({ isModalOpen, onClose, teamMembers, addGroup }) => {
    return (
        <Modal
            isOpen={isModalOpen}
            onClose={onClose}
            isCentered
            size="lg"
            motionPreset="slideInBottom"
        >
            <ModalOverlay
                bg="blackAlpha.300"
                backdropFilter="blur(1px)"
            />
            <GroupModal onClose={onClose} teamMembers={teamMembers} addGroup={addGroup}/>
        </Modal>
    );
}

const GroupModal = ({ onClose, teamMembers, addGroup }) => {
    const [groupName, setGroupName] = React.useState("");
    const [description, setDescription] = React.useState("");
    const [groupMembers, setGroupMembers] = React.useState([]);
    const [nonGroupMembers, setNonGroupMembers] = React.useState(teamMembers);
    const [searchValue, setSearchValue] = React.useState("");
    const [filteredMembers, setFilteredMembers] = React.useState([]);

    React.useEffect(() => {
        let tempGroupMembers = filterValidGroupMembers(groupMembers)
        calculateNonGroupMembers(tempGroupMembers);
        setGroupMembers(tempGroupMembers);
    }, [teamMembers]);

    const filterValidGroupMembers = (members) => {
        return members.filter(member => {
            return teamMembers.some(teamMember => {
                return teamMember.first_name === member.first_name && teamMember.last_name === member.last_name;
            });
        });
    }

    const calculateNonGroupMembers = (existingMembers) => {
        const nonGroupMembers = teamMembers.filter(member => 
            !existingMembers.some(existingMember => existingMember.email === member.email)
        );
        setNonGroupMembers(nonGroupMembers);
    }

    const isGroupValid = () => {
        if (groupName.length == 0) {
            return false;
        }

        if (groupMembers.length < 2) {
            return false;
        }

        return true
    }

    const createGroup = () => {
        if (!isGroupValid()) {
            return;
        }

        apiClient.post('/account-settings/group-actions', {
            action: "create_group",
            create_group: {
                name: groupName,
                description: description,
                group_members: groupMembers.map(member => ({
                    first_name: member.first_name,
                    last_name: member.last_name,
                    email: member.email
                })),
            }
        }).then(({data}) => {
            if (data?.success != true) {
                console.error("[CreateGroupModal] Error creating group", data?.message);
                return;
            }
            console.log("[CreateGroupModal] Group created successfully", data);
            addGroup({
                id: data?.create_group?.group_id,
                name: groupName,
                description: description,
                members: groupMembers.map(member => ({
                    first_name: member.first_name,
                    last_name: member.last_name,
                    email: member.email
                })),
            })
            onClose();
        })
    }

    return (
        <ModalContent>
            <ModalHeader>
                <HStack
                    justifyContent="space-between"
                    alignItems="center"
                    width="100%"
                >
                    <Text
                        justifySelf={"flex-start"}
                        fontSize="xl"
                        fontWeight="bold"
                        fontFamily={"sans-serif"}
                        color={"#1d1d1d"}
                    >Create Group</Text>
                    <IconButton
                        justifySelf={"flex-end"}
                        sx={{ 
                            paddingX:0, 
                            paddingY:0,
                            backgroundColor:"transparent",
                            "&:hover":{backgroundColor:"transparent"}
                        }}
                        icon={
                            <Close sx={{ padding:0, fontSize: 24, color: "#5d5d5d" }}/>
                        }
                        onClick={onClose}
                    />
                </HStack>
            </ModalHeader>
            <ModalBody>
                <VStack
                    alignItems="flex-start"
                    justifyContent="flex-start"
                    width="100%"
                    spacing={0}
                >
                    <HStack
                        alignItems="center"
                        spacing={1}
                        marginBottom={"5px"}
                    >
                        <Text fontSize="14px" fontWeight={"semibold"} color="#1d1d1d" lineHeight={"22px"}>
                            Group Name
                        </Text>
                        <Text 
                            fontSize="12px" color="#7d7d7d" fontWeight={"normal"} lineHeight={"22px"}> (required)
                        </Text>
                    </HStack>
                    <Box
                        mb="4"
                        w={"65%"}
                    >
                        <Input 
                            placeholder="Group Name" 
                            size="md"
                            value={groupName}
                            onChange={(e) => setGroupName(e.target.value)}
                        />
                    </Box>
                    <Text mb="4px" fontSize="14px" fontWeight={"semibold"} color="#1d1d1d">
                        Description
                    </Text>
                    <Box
                        mb="4"
                        w={"90%"}
                    >
                        <Input 
                            placeholder="Description"
                            size="md"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                        />
                    </Box>
                    <NewAndExistingMembers
                        searchValue={searchValue}
                        setSearchValue={setSearchValue}
                        filteredMembers={filteredMembers}
                        setFilteredMembers={setFilteredMembers}
                        setGroupMembers={setGroupMembers}
                        groupMembers={groupMembers}
                        setNonGroupMembers={setNonGroupMembers}
                        nonGroupMembers={nonGroupMembers}
                    />
                </VStack>
            </ModalBody>
            <ModalFooter>
                <Button
                    bgColor={"#E5E5E5"}
                    color={"#000"}
                    _hover={{ bgColor: "#E5E5E5" }}
                    onClick={onClose}
                    marginRight={"2px"}
                >
                    Discard
                </Button>
                <Button
                    bgColor={"#2584FD"}
                    color={"#fff"}
                    _hover={{ bgColor: "#2584FD" }}
                    onClick={createGroup}
                    marginLeft={"2px"}
                    isDisabled={!isGroupValid()}
                >
                    Create Group
                </Button>
            </ModalFooter>
        </ModalContent>
    );
}

export const NewAndExistingMembers = ({
    searchValue,
    setSearchValue,
    filteredMembers,
    setFilteredMembers,
    setGroupMembers,
    groupMembers,
    setNonGroupMembers,
    nonGroupMembers,
    disableAddMember = false
}) => {
    return (
        <>
        {
            !disableAddMember && (
                <>
                    <HStack
                        alignItems="center"
                        spacing={1}
                        marginBottom={"5px"}
                    >
                        <Text fontSize="14px" fontWeight={"semibold"} color="#1d1d1d" lineHeight={"22px"}>
                            Add Members
                        </Text>
                        <Text 
                            fontSize="12px" color="#7d7d7d" fontWeight={"normal"} lineHeight={"22px"}> (min 2)
                        </Text>
                    </HStack>
                    <Box mb="4" w={"90%"}>
                        <Input
                            placeholder="Type to search members"
                            onChange={(e) => {
                                const value = e.target.value.toLowerCase();
                                setSearchValue(value);
                                const tempFilteredMembers = nonGroupMembers.filter(member => 
                                    (member.first_name.toLowerCase().includes(value) || 
                                        member.last_name.toLowerCase().includes(value)) ||
                                    member.email.toLowerCase().includes(value)
                                );
                                setFilteredMembers(tempFilteredMembers);
                            }}
                        />
                        {searchValue?.length > 0 && filteredMembers?.length > 0 && (
                            <Box
                                mt="2"
                                border="1px solid #e5e5e5"
                                borderRadius="4px"
                                maxHeight="150px"
                                overflowY="scroll"
                                data-scrollable="true"
                                bg="white"
                            >
                                {filteredMembers.map((member, index) => (
                                    <Box
                                        key={index}
                                        p="2"
                                        _hover={{ bg: "#f0f0f0", cursor: "pointer" }}
                                        onClick={() => {
                                            setGroupMembers([...groupMembers, member]);
                                            setNonGroupMembers(nonGroupMembers.filter(m => m !== member));
                                        }}
                                    >
                                        <VStack
                                            alignItems="flex-start"
                                            justifyContent="flex-start"
                                            width="100%"
                                            spacing={0}
                                        >
                                            <Text fontSize="14px" color="#1d1d1d">
                                                {member.first_name} {member.last_name}
                                            </Text>
                                            <Text fontSize="12px" color="#7d7d7d">
                                                {member.email}
                                            </Text>                                                
                                        </VStack>
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Box>
                </>
            )
        }
            {
                groupMembers?.length > 0 && (
                    <Box mb="4" w={"100%"}>
                        <Text fontSize="14px" fontWeight={"semibold"} color="#1d1d1d">
                            Selected Members:
                        </Text>
                        <HStack wrap="wrap" spacing="4px" maxH={"200px"} overflowY={"scroll"} data-scrollable="true">
                            {groupMembers.map((member, index) => (
                                <Box
                                    key={index}
                                    p="2"
                                    border="1px solid #e5e5e5"
                                    borderRadius="4px"
                                    bg="#f9f9f9"
                                    display="flex"
                                    alignItems="center"
                                >

                                    <VStack
                                        alignItems="flex-start"
                                        justifyContent="flex-start"
                                        width="100%"
                                        spacing={0}
                                    >
                                        <Text fontSize="14px" color="#1d1d1d">
                                            {member.first_name} {member.last_name}
                                        </Text>
                                        <Text fontSize="12px" color="#7d7d7d">
                                            {member.email}
                                        </Text>                                                
                                    </VStack>
                                    <IconButton
                                        size="xs"
                                        sx={{
                                            paddingX:0,
                                            paddingY:0,
                                            backgroundColor:"transparent",
                                            "&:hover":{backgroundColor:"transparent"}
                                        }}
                                        icon={<Close fontSize="small" />}
                                        onClick={() => {
                                            setGroupMembers(groupMembers.filter(m => m !== member));
                                            setNonGroupMembers([...nonGroupMembers, member]);
                                        }}
                                    />
                                </Box>
                            ))}
                        </HStack>
                    </Box>
                )
            }
        </>
    )
}


export default CreateGroupModal;